/* eslint-disable */
// vue
import Vue from 'vue'
import { sync } from 'vuex-router-sync'
import App from './App/'
import router from './router'
import store from './store/'

// plugins
import VueSVGIcon from 'vue-svgicon'
import VueI18n from 'vue-i18n'
import Notifications from 'vue-notification'
import Vuelidate from 'vuelidate'
import VCalendar from 'v-calendar'

// swiper slider
import { Swiper as SwiperClass } from 'swiper'
import { Pagination, Navigation, Autoplay } from 'swiper/modules'
import getAwesomeSwiper from 'vue-awesome-swiper/dist/exporter'
import 'swiper/css/bundle'
SwiperClass.use([Pagination, Navigation, Autoplay])
Vue.use(getAwesomeSwiper(SwiperClass))

// i18n config
import i18n from './i18n/'

// all js icons import used for VueSVGIcon plugin
import './assets/icons'

// types
import { USER_MODULE_NAME } from '@/modules/User/store/types'

// vuex
import { mapState } from 'vuex'

// sentry
import * as Sentry from '@sentry/vue'
import { Integrations } from '@sentry/tracing'

// vue router store sync
sync(store, router)

/**
 * Sentry Session Replay Configuration
 *
 * Rules:
 * - E2E PRO: 100% all replays (session + error)
 * - MesDommages PRO: 1% session + 100% error
 * - DEV/PRE environments: 0% (no replays)
 */

// E2E user types for flow detection
const E2E_USER_TYPES = ['e2e', 'e2e_new']

/**
 * Get user_type from URL query string before Vue initialization
 * This allows us to configure Sentry replay rates based on flow type
 */
const getUserTypeFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('user_type') || sessionStorage.getItem('sc_userType') || 'gag_insured'
}

/**
 * Check if current flow is E2E based on user_type
 */
const isE2EFlow = () => {
  const userType = getUserTypeFromUrl()
  return E2E_USER_TYPES.includes(userType)
}

/**
 * Calculate Sentry Replay sample rates based on environment and flow type
 * @returns {Object} { sessionRate, errorRate }
 */
const getSentryReplayRates = () => {
  const sentryEnv = process.env.SENTRY_ENV

  // DEV and PRE environments: 0% replays
  if (sentryEnv === 'development' || sentryEnv === 'preproduction') {
    return { sessionRate: 0, errorRate: 0 }
  }

  // PRO environment: rates depend on flow type
  if (sentryEnv === 'pro-production') {
    if (isE2EFlow()) {
      // E2E PRO: 100% all replays
      return { sessionRate: 1.0, errorRate: 1.0 }
    } else {
      // MesDommages PRO (and other flows): 1% session + 100% error
      return { sessionRate: 0.01, errorRate: 1.0 }
    }
  }

  // Fallback for unknown environments: conservative defaults
  return { sessionRate: 0, errorRate: 0 }
}

// Get replay rates before Sentry initialization
const replayRates = getSentryReplayRates()

Vue.config.productionTip = false

// plugins - use
Vue.use(VueSVGIcon)
Vue.use(VueI18n)
Vue.use(Notifications)
Vue.use(Vuelidate)
Vue.use(VCalendar)

var host = window.location.host
var subdomain = host.split('.')[1]
var maDomain = true
if (subdomain === 'allianz') {
  maDomain = false
}
Vue.prototype.$maDomain = maDomain
/**
 * @module
 * @name main
 * @description Main script is executed once, before App.js, at the
 *              application start.
 *              *Routes, role, token, admin permissions* management between
 *              localStorage and app.
 */

/**
 * @function
 * @name Vue.mixin
 * @description Global mixin: stateUserIsCompanyAdmin,
 *              stateUserIsSuperAdmin are passed into all components. There all
 *              also another function called throwNotification used as a
 *              notification function wrapper.
 */
Vue.mixin({
  /**
   * @property {Boolean} stateUserIsCompanyAdmin If user is company admin.
   * @property {Boolean} stateUserIsSuperAdmin If user is super admin.
   */
  computed: {
    ...mapState(USER_MODULE_NAME, {
      stateUserIsCompanyAdmin: 'isCompanyAdmin' || false,
      stateUserIsSuperAdmin: 'isSuperAdmin' || false
    })
  },
  methods: {
    /**
     * @function
     * @name throwNotification
     * @description Wrapper for OK / KO notifications. Params containing
     *              notification text, should be already translated.
     * @param {String} title Title of notification.
     * @param {String} text Text of notification.
     * @param {String} type 'OK' throw green / check notification.
     *                      'KO' throw red / x notification.
     */
    throwNotification: function (title, text, type = 'OK') {
      const icon = type.toLowerCase()
      this.$notify({
        group: 'main',
        data: { title, text, icon },
        duration: 6000
      })
    }
  }
})

/**
 * @function
 * @name floatTofixed
 * @decription Filter - Takes value an fixed it to decimals.
 * @param {Float|Number} value Value to fix.
 * @param {Number} decimals Number of decimals to fix.
 * @return {Float|Number} Value fixed to number of decimals.
 */
Vue.filter('floatToFixed', function (value, decimals) {
  if (Math.round(value) !== value) {
    return value.toFixed(decimals)
  }
  return value
})

/**
 * @function
 * @name textOverflow
 * @description Filter - Shorten a string.
 * @param {String} value String to be shortened.
 * @param {Number} length Maximum length of returned string.
 * @return {String} String shortened or full string if its length is less than
 *                  length param.
 */
Vue.filter('textOverflow', function (value, length) {
  return value.length > length ? `${value.substr(0, length)}...` : value
})

Vue.config.errorHandler = function (err, vm, info) {
  if (process.env.NODE_ENV === 'development') {
    console.debug('err', err, vm, info)
  }
}

const app = new Vue({
  router,
  store,
  i18n,
  render: (h) => h(App)
}).$mount('#app')

const targets = [...process.env.SENTRY_TARGET]
targets.push(/^\//)

// Remove Sentry on PRE∫
Sentry.init({
  Vue,
  dsn: 'https://1ad9ba48a775428abdca0c590cc533a1@o912381.ingest.sentry.io/5905038',
  environment: process.env.SENTRY_ENV,
  normalizeDepth: 6,
  integrations: [
    Sentry.browserTracingIntegration({
      router,
      tracePropagationTargets: targets,
      trackComponents: ['InputFile', 'FileImg', 'otp']
    }),
    Sentry.replayIntegration()
  ],
  beforeSend(event, hint) {
    const fatalErrors = ['TypeError', 'SyntaxError', 'ReferenceError', 'InternalError']

    if (event.exception && event.exception.values.length > 0) {
      const exception = event.exception.values[0]
      if (fatalErrors.includes(exception.type)) {
        // Change the event level to fatal
        event.level = 'fatal'
      }
    }

    return event;
  },
  // Session Replay - Dynamic rates based on environment and flow type
  // E2E PRO: 100% all | MesDommages PRO: 1% session + 100% error | DEV/PRE: 0%
  replaysSessionSampleRate: replayRates.sessionRate,
  replaysOnErrorSampleRate: replayRates.errorRate,
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
  allowUrls: targets,
})

/*
 * ONLY available during E2E tests can let us accesing to the state
 * within a test
 */
if (window.Cypress) {
  window.app = app
}
